/* eslint-disable @next/next/no-img-element */
import { FC, useState } from 'react';

import { MdModeEditOutline } from 'react-icons/md';
import { BsFillTrashFill } from 'react-icons/bs';

import { useTheme } from 'styled-components';
import { Switch, Tooltip } from '@mui/material';

import { useMediaQuery } from '~/common/hooks/use-media-query';
import { Typography } from '~/common/components/Typograph';
import { CircleIconButton } from '~/common/components/CircleIconButton';

import {
  ActionsContainer,
  StyContainer,
  StyTable,
  StyTableOuter,
  StyTableWrapper,
  StyTd,
  StyTh,
  StyThead,
  StyTr,
} from './styles';
import { PromptMediaCarousel } from '../PromptMediaCarousel';
import { DeletePromptConfirmation } from '../DeletePromptConfirmation';
import { IPromptListItem } from '../../interfaces/i-prompt-list-item';
import { PromptTypesFormattedEnum } from '../../enums/prompt-types-enum';

interface IAdminPromptListProps {
  promtps: IPromptListItem[];
  deletePrompt: (promptId: string) => Promise<void>;
  editPrompt: (promptId: string) => void;
  togglePromptApproval: (prompt: IPromptListItem) => Promise<void>;
}
export const AdminPromptList: FC<IAdminPromptListProps> = ({
  promtps,
  deletePrompt,
  editPrompt,
  togglePromptApproval,
}) => {
  const isMobile = useMediaQuery('(max-width: 685px)');
  const theme = useTheme();
  const [deletingPromptId, setDeletingPromptId] = useState<string | null>(null);

  return (
    <>
      <DeletePromptConfirmation
        open={deletingPromptId !== null}
        close={() => setDeletingPromptId(null)}
        title={promtps.find((where) => where.id === deletingPromptId)?.title}
        confirm={async () => {
          await deletePrompt(deletingPromptId).finally(() =>
            setDeletingPromptId(null)
          );
        }}
      />

      <StyContainer>
        <StyTableOuter>
          <StyTableWrapper>
            <StyTable>
              <StyThead>
                <StyTh
                  align="left"
                  style={{ width: isMobile ? '30px' : '80px' }}
                >
                  <Typography.Label size="x-small" color="neutral0">
                    {isMobile ? '' : 'Mídia'}
                  </Typography.Label>
                </StyTh>

                <StyTh
                  align="left"
                  style={{
                    width: isMobile ? '160px' : '340px',
                    ...(isMobile && {
                      paddingLeft: '6px',
                    }),
                  }}
                >
                  <Typography.Label size="x-small" color="neutral0">
                    Título
                  </Typography.Label>
                </StyTh>

                <StyTh align="left">
                  <Typography.Label size="x-small" color="neutral0">
                    Categoria
                  </Typography.Label>
                </StyTh>

                <StyTh
                  align="left"
                  style={{
                    width: '60px',
                    ...(isMobile && {
                      paddingLeft: '6px',
                    }),
                  }}
                >
                  <Typography.Label size="x-small" color="neutral0">
                    Likes
                  </Typography.Label>
                </StyTh>

                <StyTh
                  align="right"
                  style={{
                    width: '80px',
                    ...(isMobile && {
                      paddingLeft: '6px',
                    }),
                  }}
                >
                  <Typography.Label size="x-small" color="neutral0">
                    Ações
                  </Typography.Label>
                </StyTh>
              </StyThead>

              <tbody>
                {promtps.map((prompt) => {
                  return (
                    <StyTr key={prompt.id}>
                      <StyTd
                        align="left"
                        style={{
                          paddingLeft: '10px',
                        }}
                      >
                        <div
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                          }}
                        >
                          <PromptMediaCarousel
                            mediaUrls={prompt.mediaUrl}
                            compactMediaUrls={prompt.compactMediaUrl}
                            type={prompt.type}
                            alt={prompt.title}
                            fallbackToDefault
                            objectFit="cover"
                            hoverScale={1}
                          />
                        </div>
                      </StyTd>

                      <StyTd
                        align="left"
                        {...(isMobile && {
                          style: { paddingLeft: '6px' },
                        })}
                      >
                        <div
                          style={{
                            width: isMobile ? '160px' : '340px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Typography.Label size="x-small" color="neutral0">
                            {prompt.title}
                          </Typography.Label>
                        </div>
                      </StyTd>

                      <StyTd align="left">
                        <Typography.Label size="x-small" color="neutral0">
                          {PromptTypesFormattedEnum[prompt.type]}
                        </Typography.Label>
                      </StyTd>

                      <StyTd align="left">
                        <Typography.Label size="x-small" color="neutral0">
                          {prompt.likes || '0'}
                        </Typography.Label>
                      </StyTd>

                      <StyTd align="right">
                        <ActionsContainer>
                          <CircleIconButton
                            icon={MdModeEditOutline}
                            tooltip="Editar"
                            size={isMobile ? 26 : 30}
                            iconSize={isMobile ? 14 : 18}
                            onClick={() => editPrompt(prompt.id)}
                            backgroundColor={theme.neutral800}
                            iconColor="neutral0"
                            hoverFilterBackground
                            tooltipPlacement="top"
                          />

                          <CircleIconButton
                            icon={BsFillTrashFill}
                            tooltip="Apagar"
                            size={isMobile ? 26 : 30}
                            iconSize={isMobile ? 14 : 18}
                            onClick={() => setDeletingPromptId(prompt.id)}
                            backgroundColor={theme.neutral800}
                            iconColor="red"
                            hoverFilterBackground
                            tooltipPlacement="top"
                          />

                          <Tooltip
                            title={
                              prompt.approved
                                ? 'Desaprovar prompt'
                                : 'Aprovar prompt'
                            }
                          >
                            <Switch
                              checked={prompt.approved}
                              onChange={() => togglePromptApproval(prompt)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: theme.primary, // cor do thumb quando ativo
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                  {
                                    backgroundColor: theme.primary, // cor do track quando ativo
                                  },
                                '& .MuiSwitch-track': {
                                  backgroundColor: '#e5e7eb', // cor quando inativo
                                },
                              }}
                            />
                          </Tooltip>
                        </ActionsContainer>
                      </StyTd>
                    </StyTr>
                  );
                })}
              </tbody>
            </StyTable>
          </StyTableWrapper>
        </StyTableOuter>
      </StyContainer>
    </>
  );
};
